import React from 'react';
import {
  Monitor, Clock, DollarSign, FileText, Mail, Database, ListChecks, Check,
  Cloud,
} from 'lucide-react';

// Read-only proposal summary — visual parity with MspProposalSummary in
// frontend/src/modules/crm/MspProposalForm.jsx. The CRM version lives in a
// separate React app and pulls in CRM-specific styles, so the rendering is
// mirrored here against the portal's own .proposal-summary-* CSS classes.
//
// Expected `data` shape (matches the GET /api/portal/proposals/{path_id}
// response's `proposal` field — same flat shape the CRM form saves):
//   { msp_managed, hourly_rate, ehr_hosting?, email_hosting?, onboarding, notes }
// msp_managed.billing_method = 'per_device' | 'flat_rate' selects between
// workstation/server × rate vs. monthly_fee.

function formatMoney(v) {
  if (v == null || v === '') return '$0.00';
  return `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function calcDiscount(base, discount, discountType) {
  if (!discount) return base;
  const d = Number(discount) || 0;
  if (discountType === 'percent') return base * (1 - d / 100);
  return Math.max(0, base - d);
}

export default function MspProposalSummary({ data }) {
  if (!data) return <div className="empty-messages">No proposal data yet.</div>;
  const m  = data.msp_managed   || {};
  const hr = data.hourly_rate   || {};
  const eh = data.ehr_hosting   || null;
  const em = data.email_hosting || null;
  const ob = data.onboarding    || {};

  const isFlatRate = m.billing_method === 'flat_rate';

  const managedBase = isFlatRate
    ? (Number(m.monthly_fee) || 0)
    : (Number(m.workstation_rate) || 0) * (Number(m.workstation_count) || 0)
      + (Number(m.server_rate)    || 0) * (Number(m.server_count)      || 0);
  const managedTotal = calcDiscount(managedBase, m.discount, m.discount_type);

  const ehrBase = eh ? (Number(eh.rate_per_connection) || 0) * (Number(eh.concurrent_connections) || 0) : 0;
  const ehrTotal = eh ? calcDiscount(ehrBase, eh.discount, eh.discount_type) : 0;

  const emailBase = em ? (Number(em.rate_per_user) || 0) * (Number(em.user_count) || 0) : 0;
  const emailTotal = em ? calcDiscount(emailBase, em.discount, em.discount_type) : 0;

  // Boot to Cloud Backup — may be an optional add-on. It joins the Monthly
  // Recurring headline once it's actually part of the deal: either it's
  // non-optional, or the client chose Add. Pending/declined stay out of the
  // total (the add-on chooser card handles the pending decision).
  const cb = data.cloud_backup || null;
  const cbBase = cb ? (Number(cb.monthly_fee) || 0) : 0;
  const cbTotal = cb ? calcDiscount(cbBase, cb.discount, cb.discount_type) : 0;
  const cbDeclined = !!(cb && cb._is_optional && cb._client_decision === false);
  const cbPending = !!(cb && cb._is_optional
    && cb._client_decision !== true && cb._client_decision !== false);
  const cbInTotal = !!(cb && cbBase > 0 && !cbDeclined && !cbPending);

  const monthlyTotal = managedTotal + ehrTotal + emailTotal
    + (cbInTotal ? cbTotal : 0);
  const oneTimeTotal = calcDiscount(Number(ob.fee) || 0, ob.discount, ob.discount_type);

  return (
    <div className="proposal-summary">
      <div className="proposal-summary-totals">
        <div className="proposal-summary-total">
          <span className="proposal-summary-total-label">Monthly Recurring</span>
          <span className="proposal-summary-total-value">{formatMoney(monthlyTotal)}/mo</span>
        </div>
        <div className="proposal-summary-total">
          <span className="proposal-summary-total-label">One-Time Onboarding</span>
          <span className="proposal-summary-total-value">{formatMoney(oneTimeTotal)}</span>
        </div>
      </div>

      <div className="proposal-summary-section">
        <div className="proposal-summary-section-title">
          <Monitor size={12} /> Managed Services — {m.plan_tier || 'Fully Managed'}
          {isFlatRate && (
            <span style={{ marginLeft: 8, fontWeight: 400, color: '#666' }}>(flat rate)</span>
          )}
        </div>
        <div className="proposal-summary-grid">
          {isFlatRate ? (
            <div className="proposal-summary-field">
              <span className="summary-label">Monthly Fee:</span>{' '}
              <span className="summary-value">{formatMoney(m.monthly_fee)}/mo</span>
            </div>
          ) : (
            <>
              {Number(m.workstation_count) > 0 && (
                <div className="proposal-summary-field">
                  <span className="summary-label">Workstations:</span>{' '}
                  <span className="summary-value">
                    {m.workstation_count} × {formatMoney(m.workstation_rate)} ={' '}
                    {formatMoney((Number(m.workstation_rate) || 0) * (Number(m.workstation_count) || 0))}
                  </span>
                </div>
              )}
              {Number(m.server_count) > 0 && (
                <div className="proposal-summary-field">
                  <span className="summary-label">Servers:</span>{' '}
                  <span className="summary-value">
                    {m.server_count} × {formatMoney(m.server_rate)} ={' '}
                    {formatMoney((Number(m.server_rate) || 0) * (Number(m.server_count) || 0))}
                  </span>
                </div>
              )}
            </>
          )}
          {m.plan_tier === 'Limited Managed' && Number(m.included_hours) > 0 && (
            <div className="proposal-summary-field">
              <span className="summary-label">Included support:</span>{' '}
              <span className="summary-value">{m.included_hours} hr/mo</span>
            </div>
          )}
          {Number(m.discount) > 0 && (
            <div className="proposal-summary-field">
              <span className="summary-label">Discount:</span>{' '}
              <span className="summary-value discount">
                {m.discount_type === 'percent' ? `${m.discount}%` : formatMoney(m.discount)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* What's Included — what the plan covers. Scope only, no pricing. */}
      {(data.whats_included || []).length > 0 && (
        <div className="proposal-summary-section">
          <div className="proposal-summary-section-title"><ListChecks size={12} /> What's Included</div>
          <ul className="msp-included-list">
            {data.whats_included.map((item, i) => (
              <li key={i}><Check size={12} /> {item}</li>
            ))}
          </ul>
        </div>
      )}

      {Number(hr.hourly_rate) > 0 && (
        <div className="proposal-summary-section">
          <div className="proposal-summary-section-title"><Clock size={12} /> Hourly Rate</div>
          <div className="proposal-summary-grid">
            <div className="proposal-summary-field">
              <span className="summary-label">Rate:</span>{' '}
              <span className="summary-value">{formatMoney(hr.hourly_rate)}/hr</span>
            </div>
          </div>
        </div>
      )}

      {eh && Number(eh.concurrent_connections) > 0 && (
        <div className="proposal-summary-section">
          <div className="proposal-summary-section-title">
            <Database size={12} /> EHR Hosting{eh.ehr_system_name ? ` — ${eh.ehr_system_name}` : ''}
          </div>
          <div className="proposal-summary-grid">
            <div className="proposal-summary-field">
              <span className="summary-label">Connections:</span>{' '}
              <span className="summary-value">
                {eh.concurrent_connections} × {formatMoney(eh.rate_per_connection)} = {formatMoney(ehrBase)}/mo
              </span>
            </div>
            {Number(eh.discount) > 0 && (
              <div className="proposal-summary-field">
                <span className="summary-label">Discount:</span>{' '}
                <span className="summary-value discount">
                  {eh.discount_type === 'percent' ? `${eh.discount}%` : formatMoney(eh.discount)}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {em && Number(em.user_count) > 0 && (
        <div className="proposal-summary-section">
          <div className="proposal-summary-section-title"><Mail size={12} /> Email Hosting</div>
          <div className="proposal-summary-grid">
            <div className="proposal-summary-field">
              <span className="summary-label">Users:</span>{' '}
              <span className="summary-value">
                {em.user_count} × {formatMoney(em.rate_per_user)} = {formatMoney(emailBase)}/mo
              </span>
            </div>
            {Number(em.discount) > 0 && (
              <div className="proposal-summary-field">
                <span className="summary-label">Discount:</span>{' '}
                <span className="summary-value discount">
                  {em.discount_type === 'percent' ? `${em.discount}%` : formatMoney(em.discount)}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {cb && cbBase > 0 && !cbDeclined && (
        <div className="proposal-summary-section">
          <div className="proposal-summary-section-title"><Cloud size={12} /> Boot to Cloud Backup{cbPending ? ' (optional add-on)' : ''}</div>
          <div className="proposal-summary-grid">
            <div className="proposal-summary-field">
              <span className="summary-label">Monthly fee:</span>{' '}
              <span className="summary-value">
                {Number(cb.discount) > 0 ? (
                  <>
                    <s style={{ color: '#94a3b8', fontWeight: 400 }}>{formatMoney(cbBase)}</s>
                    {' '}<strong>{formatMoney(cbTotal)}/mo</strong>
                  </>
                ) : (
                  <>{formatMoney(cbTotal)}/mo</>
                )}
              </span>
            </div>
            {Number(cb.discount) > 0 && (
              <div className="proposal-summary-field">
                <span className="summary-label">Discount:</span>{' '}
                <span className="summary-value discount">
                  {cb.discount_type === 'percent' ? `${cb.discount}%` : formatMoney(cb.discount)}
                </span>
              </div>
            )}
            {cbPending && (
              <div className="proposal-summary-field">
                <span className="summary-value" style={{ color: '#64748b', fontWeight: 400 }}>
                  Added to your monthly total only if you choose Add below.
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {Number(ob.fee) > 0 && (
        <div className="proposal-summary-section">
          <div className="proposal-summary-section-title"><DollarSign size={12} /> One-Time Onboarding</div>
          <div className="proposal-summary-grid">
            <div className="proposal-summary-field">
              <span className="summary-label">Fee:</span>{' '}
              <span className="summary-value">{formatMoney(ob.fee)}</span>
            </div>
            {Number(ob.discount) > 0 && (
              <div className="proposal-summary-field">
                <span className="summary-label">Discount:</span>{' '}
                <span className="summary-value discount">
                  {ob.discount_type === 'percent' ? `${ob.discount}%` : formatMoney(ob.discount)}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {data.notes && (
        <div className="proposal-summary-section">
          <div className="proposal-summary-section-title"><FileText size={12} /> Notes</div>
          <div className="proposal-summary-notes">{data.notes}</div>
        </div>
      )}
    </div>
  );
}
